/* import __COLOCATED_TEMPLATE__ from './project-details-admin-actions.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { showModal, closeModal } from 'sh-www/utils/modal-controls';
import { htmlSafe } from '@ember/template';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { APPLICATION } from "sh-www/models/application";
import { PROJECT_LINE_ITEM } from "sh-www/models/project-line-item";
import { PROJECT_DETAILS__APPLICATION_REOPENABLE_BY_ADMIN } from "sh-www/constants/routes/project-details";
import AdminProjectCancelModal from 'sh-www/components/modals/admin/project-cancel';

const CLASS_NAME = 'SH-c-project-details-admin-actions';

class ProjectDetailsAdminActions extends Component {
  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-connection-commands') companyConnectionCommands;
  @service errorTracker;
  @service('commands/project-line-item-commands') projectLineItemCommandService;
  @service session;
  @service store;

  get className() {
    return CLASS_NAME;
  }

  get adminStatusText() {
    const { application } = this.args;
    const { buyer, seller } = application;
    const sellerFullName = seller.fullName;
    const buyerFullName = buyer.fullName;

    switch (application.status) {
      case APPLICATION.STATUS.PENDING:
        return application.isNeedsOffer
          ? `Waiting for ${buyerFullName}'s offer`
          : `Waiting for ${buyerFullName}'s response`;
      case APPLICATION.STATUS.PUBLISHER_APPROVED:
        return `Waiting for ${sellerFullName} to accept ${buyerFullName}'s offer`;
      case APPLICATION.STATUS.PRODUCER_APPROVED:
        return `Waiting for ${buyerFullName} to accept ${sellerFullName}'s offer`;
      case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
        return `Waiting for ${sellerFullName} to sign the contract`;
      case APPLICATION.STATUS.ACTIVE:
        return 'Project is in progress';
      case APPLICATION.STATUS.COMPLETE_PRODUCER:
        return `Waiting for ${buyerFullName} to confirm completion`;
      case APPLICATION.STATUS.CANCEL_PENDING:
        return `Waiting for ${sellerFullName} to confirm cancellation`;
      case APPLICATION.STATUS.COMPLETE_PUBLISHER:
        return `Waiting for ${sellerFullName} to confirm completion`;
      case APPLICATION.STATUS.COMPLETED:
      case APPLICATION.STATUS.PAID:
        return application.projectdigest?.id
          ? htmlSafe(`Project complete. <a target="_blank" href="/${application.seller.slug}/?project=${application.projectdigest.id}"></a>`)
          : 'Project complete';
      case APPLICATION.STATUS.DECLINED:
        return 'Project declined';
      case APPLICATION.STATUS.CANCELLED:
        return 'Project cancelled';
    }
    return 'Unknown';
  }

  get expenseToggleText() {
    return this.args.application.isAllowExpenses
      ? 'Disable Expenses'
      : 'Enable Expenses';
  }

  get canReOpenProject() {
    return PROJECT_DETAILS__APPLICATION_REOPENABLE_BY_ADMIN.includes(this.args.application.status);
  }

  get canCancelProject() {
    return this.args.application.status == APPLICATION.STATUS.ACTIVE;
  }

  get actionsDisabled() {
    return this.args.loadPaymentData.isRunning;
  }

  get toggleSelectText() {
    return this.args.application.select
      ? 'Remove Select'
      : 'Make Select';
  }

  @task
  *updatePriceTask(price) {
    const { application, loadPaymentData, loadThreadData } = this.args;
    const baseLineItem = application.projectLineItems.find(lineItem => lineItem.type === PROJECT_LINE_ITEM.TYPES.BASE);

    try {
      const response = yield this.projectLineItemCommandService.updateProjectLineItem(
        application,
        baseLineItem,
        price,
        baseLineItem.description
      );
      this.store.pushPayload(response);
      this.applicationStateManager.addToast('Project price changed');
      loadPaymentData.perform(application);
      loadThreadData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem while trying to change project price. Please try again.');
      this.errorTracker.error(e, this, `Error changing project price for application id "${application.id}" active`);
    }
  }

  @task
  *toggleExpensesEnabled(application) {
    const isEnabled = !application.isAllowExpenses;
    try {
      yield this.applicationCommandService.setExpensesEnabled(application, isEnabled);
      this.applicationStateManager.addToast(isEnabled ? 'Expenses enabled' : 'Expenses disabled');
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast(`Failed to ${isEnabled ? 'enable' : 'disable'} expenses. Try again or contact support for assistance.`);
    }
  }

  @action
  makeFMS() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'Are you sure?',
      bodyText: 'This freelancer will become an FMS hire on this project, and any other un-paid projects between the freelancer and company.<br /><br />It WILL NOT update any paid projects or finalized advances, and you should repeat this action on any others you want included.',
      confirmAction: () => {
        this.companyConnectionCommands.updateApplicationFMSStatus(application, true).then(() => {
          closeModal(this);
          this.applicationStateManager.addToast('User invited, fees updated');
          loadPaymentData.perform(application);
          loadThreadData.perform(application);
        }).catch(e => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error connecting those users. Please refresh and try again.',
            'updateUserConnection -- error connecting: ' + application.buyer.id + ' to: ' + application.seller.id,
          );
        });
      }
    });
  }

  @action
  makeNonFMS() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: 'Are you sure?',
      bodyText: 'This freelancer will become an NON-FMS hire on this project, and any other un-paid projects between the freelancer and company.<br /><br />It WILL NOT update any paid projects or finalized advances, and you should repeat this action on any others you want included.',
      confirmAction: () => {
        this.companyConnectionCommands.updateApplicationFMSStatus(application, false).then(() => {
          closeModal(this);
          this.applicationStateManager.addToast('User un-invited, fees updated');
          loadPaymentData.perform(application);
          loadThreadData.perform(application);
        }).catch(e => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error disconnecting those users. Please refresh and try again.',
            'updateUserConnection -- error disconnecting: ' + application.buyer.id + ' to: ' + this.application.seller.id,
            this
          );
        });
      }
    });
  }

  @action
  onToggleSelectClicked() {
    const { application, loadPaymentData } = this.args;

    const title = application.select
      ? 'Remove Project Select Status?'
      : 'Make Project Select?';

    const body = application.select
      ? `Are you sure you want to remove the Select status from project "${application.title}"?`
      : `Are you sure you want to make "${application.title}" a Select project?`;

    showModal(this, 'confirmation', {
      isShowCancelButton: true,
      titleText: title,
      bodyText: body,
      confirmAction: () => {
        this.applicationCommandService.setSelectStatus(application, !application.select).then(() => {
          loadPaymentData.perform(application);
        }).catch((e) => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error updating the select status. Please refresh and try again.',
            'onToggleSelectClicked -- error toggling select status',
          );
        });
      },
    });
  }

  @action
  onCancelProjectClicked() {
    const { application, loadPaymentData, loadThreadData } = this.args;

    showModal(this, AdminProjectCancelModal, {
      application,
      reloadApplicationCallback: () => {
        loadPaymentData.perform(application);
        loadThreadData.perform(application);
      },
    })
  }

  @action
  onEditExpensesClicked() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    const modalData = {
      application,
      onExpensesUpdated: () => {
        this.applicationStateManager.addToast('Expenses updated');
        loadPaymentData.perform(application);
        loadThreadData.perform(application);
      }
    };

    showModal(this, 'projects/edit-expenses-modal', modalData);
  }

  @action
  onEditFeesClicked() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    const modalModel = {
      relatedModel: application,
      showBuyerFees: true,
      showSellerFees: true,
      onFeesUpdated: (madeFMS) => {
        if (madeFMS) {
          this.applicationStateManager.addToast('User invited, fees updated');
        } else if (madeFMS === false) {
          this.applicationStateManager.addToast('User un-invited, fees updated');
        } else {
          this.applicationStateManager.addToast('Fees updated');
        }
        loadPaymentData.perform(application);
        loadThreadData.perform(application);
      }
    };
    showModal(this, 'projects/edit-project-fees-modal', modalModel);
  }

  @action
  onEditPriceClicked() {
    const modalModel = {
      application: this.args.application,
      onChangePriceConfirmed: (price) => {
        this.updatePriceTask.perform(price);
      },
    };
    showModal(this, 'projects/change-project-price-modal', modalModel);
  }

  @action
  onReconcileBilling() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    showModal(this, 'confirmation', {
      titleText: 'Reconcile Billing',
      bodyText: "This will re-calculate the application's billing, and set payout, process payment, send emails, etc. if appropriate.",
      actionButtonText: 'Okay',
      isShowCancelButton: true,
      confirmAction: () => {
        this.applicationCommandService.reconcileBilling(application)
          .then(() => {
            this.applicationStateManager.addToast('Billing reconciled');
            loadPaymentData.perform(application);
            loadThreadData.perform(application);
          })
          .catch((e) => {
            handleErrorResponseWithModal(
              this,
              e,
              'There was an error reconciling billing. Please contact engineering for support.',
              `reconcileBilling -- Failing reconciling app id: ${application.id}`,
            );
          });
      },
    });
  }

  @action
  onReOpenProjectClicked() {
    const modalModel = {
      isShowCancelButton: true,
      titleText: 'Re-open project',
      bodyText: 'Are you sure you want to re-open this project?',
      confirmAction: () => {
        this.handleAdminReOpenApplication.perform(this.args.application);
      }
    };

    showModal(this, 'confirmation', modalModel);
  }

  @task
  *handleAdminReOpenApplication(application) {
    yield this.applicationCommandService.reopenApplication(application);
    this.applicationStateManager.addToast('Project marked active');
    this.args.loadPaymentData.perform(application);
    this.args.loadThreadData.perform(application);
  }
}

export default ProjectDetailsAdminActions;
